/**
 * A8: Beim Ziehen einer Karte in eine Phase mit fehlendem Eintritts-Pflichtfeld öffnet sich der
 * vorhandene Phasenwechsel-Dialog — mit Zielphase und Eingabefeld. Das Projekt wird VOLLSTÄNDIG
 * nachgeladen: MovePhaseModal schreibt `handover_ready_fields` als Ganzes, mit den Board-Daten
 * gingen die übrigen Übergabefelder verloren.
 */
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api.js';
import { Modal } from './modal.js';
import { MovePhaseModal } from './move-phase-modal.js';

// Bewusst kein Import von `Phase` aus @projektverwaltung/shared: phase-board.tsx führt an dieser
// Stelle seinen eigenen schmaleren Phase-Typ (nur phase_id/name/order, siehe KanbanLens), dem hier
// created_at/updated_at fehlen. MovePhaseModal braucht ohnehin nur diese Teilmenge.
type Phase = {
  phase_id: string;
  name: string;
  order: number;
  entry_required_fields?: string[] | null | undefined;
};

export function BoardPflichtfeldDialog({
  projectId,
  toPhaseId,
  phases,
  gate,
  onClose,
}: {
  projectId: string;
  toPhaseId: string;
  phases: Phase[];
  gate?: { from_phase: string; to_phase: string } | undefined;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const query = useQuery({
    queryKey: ['v2-project', projectId],
    queryFn: () => api.getProject(projectId),
  });
  if (query.isError) {
    return (
      <Modal open onClose={onClose} labelledBy="pflicht-fehler" panelClassName="w-full max-w-md">
        <div className="px-5 py-4 space-y-3">
          <h2 id="pflicht-fehler" className="text-sm font-medium" style={{ color: 'var(--ink)' }}>
            {t('v2.modal.move_phase_title', 'Phase wechseln')}
          </h2>
          <p className="text-sm" style={{ color: 'var(--danger)' }}>
            {t(
              'v2.board.pflicht_laden_fehler',
              'Das Projekt konnte nicht geladen werden. Bitte im Projektdetail die Phase wechseln.',
            )}
          </p>
          <button
            type="button"
            onClick={onClose}
            className="text-xs px-3 py-1.5 rounded-md"
            style={{
              background: 'var(--surface)',
              color: 'var(--ink-2)',
              border: '1px solid var(--line-soft)',
            }}
          >
            {t('v2.modal.cancel', 'Abbrechen')}
          </button>
        </div>
      </Modal>
    );
  }
  if (!query.data) return null; // kurzer Ladezustand; die Karte liegt bereits wieder in ihrer Spalte
  return (
    <MovePhaseModal
      open
      onClose={onClose}
      project={query.data}
      phases={phases}
      gate={gate}
      initialTargetPhaseId={toPhaseId}
    />
  );
}
